// Переключатель автоигры рядом с кнопкой спина.
//
// У кнопки два лица: стрелки с треугольником включают автоигру, со квадратом — выключают.
// Само решение, когда начать очередной спин, остаётся у сцены: кнопка только меняет режим.

import { Container } from 'pixi.js';

import type { UiFrame } from '../assets/catalog';
import type { TextureSheet } from '../assets/manager';

import { RoundButton, type ButtonFaces } from './button';
import { minTouchableScale, type GameLayout, viewportEdge } from './layout';
import { DESIGN_HEIGHT, DESIGN_WIDTH, EDGE_MARGIN } from './scene';
import { SPIN_SCALE } from './spinControls';

/** Кнопки стоят рядом, но не касаются своими круглыми областями нажатия. */
const BUTTON_GAP: number = 20;

/** Autoplay в нижней полосе читается как вспомогательная кнопка, наравне с info и rules. */
const AUTOPLAY_SIZE: number = 84;

/** Переключатель autoplay: активное состояние означает, что следующий доступный спин пойдёт сам. */
export class AutoplayButton {
  readonly view: Container = new Container();

  readonly #button: RoundButton;
  readonly #playFaces: ButtonFaces;
  readonly #stopFaces: ButtonFaces;
  readonly #onToggle: (active: boolean) => void;
  readonly #spinRadius: number;

  #active = false;

  constructor(ui: TextureSheet<UiFrame>, onToggle: (active: boolean) => void) {
    this.#playFaces = { idle: ui.autoplayPlayIdle, hover: ui.autoplayPlayHover, active: ui.autoplayPlayActive };
    this.#stopFaces = { idle: ui.autoplayStopIdle, hover: ui.autoplayStopHover, active: ui.autoplayStopActive };
    this.#onToggle = onToggle;

    this.#button = new RoundButton(this.#playFaces, () => this.#toggle());

    this.#spinRadius = ui.spinIdle.width * SPIN_SCALE / 2;
    const spinX: number = DESIGN_WIDTH / 2 - EDGE_MARGIN - this.#spinRadius;
    const scale: number = AUTOPLAY_SIZE / this.#button.radius / 2;
    const radius: number = this.#button.radius * scale;
    this.#button.view.scale.set(scale);
    this.#button.view.position.set(spinX - this.#spinRadius - BUTTON_GAP - radius, DESIGN_HEIGHT / 2 - EDGE_MARGIN - radius);

    this.view.addChild(this.#button.view);
  }

  /** Двигает плавное затухание (сейчас кнопка всегда доступна, но контракт кнопок единый). */
  update(seconds: number): void {
    this.#button.update(seconds);
  }

  /** На мобильном autoplay стоит рядом со spin, а не в конце нижней строки. */
  layout(layout: GameLayout): void {
    const { bottom, bounds, composition, viewport } = layout;
    const mobile: boolean = composition === 'mobile';
    const baseScale: number = AUTOPLAY_SIZE / this.#button.radius / 2;
    const scale: number = mobile ? minTouchableScale(viewport, this.#button.radius, 26, baseScale) : baseScale;
    const radius: number = this.#button.radius * scale;
    const spinRadius: number = mobile
      ? (this.#spinRadius / SPIN_SCALE) * minTouchableScale(viewport, this.#spinRadius / SPIN_SCALE, 32, SPIN_SCALE)
      : this.#spinRadius;
    const edge: number = mobile ? viewportEdge(viewport) : EDGE_MARGIN;
    const gap: number = mobile ? BUTTON_GAP / viewport.scale : BUTTON_GAP;

    this.#button.view.scale.set(scale);
    this.#button.view.position.set(
      bounds.right - edge - spinRadius * 2 - gap - radius,
      mobile ? bottom.buttonBottom - radius : bounds.bottom - EDGE_MARGIN - radius,
    );
  }

  get active(): boolean {
    return this.#active;
  }

  #toggle(): void {
    this.#active = !this.#active;
    // RoundButton не выпадает из hit-test при смене faces, поэтому сразу оставляет hover/press,
    // если курсор всё ещё над тем же кругом.
    this.#button.faces = this.#active ? this.#stopFaces : this.#playFaces;
    this.#onToggle(this.#active);
  }
}
